// =============================================================================
// Image-source helpers used by ProfileCard / ProfileDashboard / ProfileDetails.
//   • Storage paths (e.g. "relaxora/uploads/xyz.jpg") are converted to a fully
//     qualified backend URL so the <img> tag can fetch them after deployment.
//   • Full URLs (http/https/blob/data) pass through unchanged.
//   • Missing / invalid values resolve to a clean dark inline-SVG placeholder.
// =============================================================================

import { API_URL } from "@/lib/api";

// Tiny dark gradient placeholder inlined as data-URI so it always loads,
// even with no network. Used both as a default src and as the onError fallback.
export const IMG_PLACEHOLDER =
  "data:image/svg+xml;utf8," + encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 400'>
       <defs>
         <linearGradient id='g' x1='0' y1='0' x2='0' y2='1'>
           <stop offset='0%'  stop-color='#1c1917'/>
           <stop offset='100%' stop-color='#0a0a0a'/>
         </linearGradient>
       </defs>
       <rect width='300' height='400' fill='url(#g)'/>
       <g fill='none' stroke='#3f3f46' stroke-width='1.5'>
         <circle cx='150' cy='170' r='42'/>
         <path d='M80 320 q70 -70 140 0'/>
       </g>
     </svg>`
  );

/**
 * Return a fetchable <img src> for a possibly-storage-path value.
 *   resolveImageUrl("relaxora/uploads/x.jpg") → "https://api…/api/files/relaxora/uploads/x.jpg"
 *   resolveImageUrl("https://cdn/x.jpg")     → "https://cdn/x.jpg"
 *   resolveImageUrl(null)                    → IMG_PLACEHOLDER
 */
export function resolveImageUrl(value) {
  if (!value || typeof value !== "string") return IMG_PLACEHOLDER;
  const v = value.trim();
  if (!v) return IMG_PLACEHOLDER;
  if (/^(https?:|blob:|data:)/i.test(v)) return v;
  if (v.startsWith("/")) return v;
  // Internal storage path — route through the backend's authenticated file serve.
  return `${API_URL}/files/${v}`;
}

/**
 * onError handler that swaps the broken image to the placeholder. Prevents
 * the browser's default broken-image icon from ever appearing.
 */
export function onImageError(e) {
  if (e?.target && e.target.src !== IMG_PLACEHOLDER) {
    e.target.src = IMG_PLACEHOLDER;
  }
}

/**
 * Resolve the best display image for a profile, preferring (in order):
 *   1) main_photo (legacy / mirrored field)
 *   2) gallery[is_main]
 *   3) first gallery image
 *   4) first item in photos[]
 *   5) placeholder
 */
export function profileMainImage(p) {
  if (!p) return IMG_PLACEHOLDER;
  if (p.main_photo) return resolveImageUrl(p.main_photo);
  const gallery = Array.isArray(p.gallery) ? p.gallery : [];
  const main = gallery.find((g) => g?.is_main && g?.type === "image");
  if (main?.url) return resolveImageUrl(main.url);
  const firstImg = gallery.find((g) => g?.type === "image");
  if (firstImg?.url) return resolveImageUrl(firstImg.url);
  if (Array.isArray(p.photos) && p.photos[0]) return resolveImageUrl(p.photos[0]);
  return IMG_PLACEHOLDER;
}
